import { cloneElement, ReactNode, useEffect } from 'react';
import { ColumnMeta, RowData } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { DiffStatus } from 'components/DataTable/types';

import { CellShell } from '../CellShell/CellShell';
import { deriveViewCellVariant, ViewCellVariant } from '../cellVariant';

type ViewWrapperCellProps = {
  children: ReactNode;
  className?: string;
  fieldId: string;
  isHighlighted: boolean;
  meta: ColumnMeta<RowData, unknown> | undefined;
  cell?: unknown;
  diffStatus?: DiffStatus;
  originalValue?: string | number | boolean | null;
  formattedOriginalValue?: ReactNode;
  textAlign?: 'left' | 'right' | 'center';
  wasAIEdited?: boolean;
  hasWarning?: boolean;
};

export const ViewWrapperCell = ({
  className,
  isHighlighted,
  fieldId,
  children,
  meta,
  cell,
  diffStatus,
  originalValue,
  formattedOriginalValue,
  textAlign,
  wasAIEdited,
  hasWarning,
}: ViewWrapperCellProps) => {
  const { service, NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext<WebEDIAnnotation>();
  const error = service.useItemByFieldPath(fieldId);
  const fieldState = service.useFieldStateFromAnnotations(fieldId);
  const setCurrentItem = service.setCurrentItem;
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      setCurrentItem(error);
    }
  }, [error, setCurrentItem, showNavigableItemPopover]);

  const variant = deriveViewCellVariant({
    fieldState,
    diffStatus,
    wasAIEdited: Boolean(wasAIEdited),
    isHighlighted,
    hasWarning,
  });

  const rightAddon = meta?.rightAddon ? cloneElement(meta.rightAddon, { cell }) : undefined;

  return (
    <>
      <CellShell
        variant={variant}
        mode="view"
        isFocused={showNavigableItemPopover}
        fieldId={fieldId}
        textAlign={textAlign}
        className={className}
        rightAddon={rightAddon}
        onFocus={() => setShowNavigableItemPopover(true)}
        onBlur={() => setShowNavigableItemPopover(false)}
        ref={refs.setReference}
      >
        <ViewCellContent
          formattedOriginalValue={formattedOriginalValue}
          originalValue={originalValue}
          variant={variant}
        >
          {children}
        </ViewCellContent>
      </CellShell>
      {showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          annotation={error}
          viewMode
          ref={refs.setFloating}
          style={floatingStyles}
          fieldDescription={meta?.description}
        />
      )}
    </>
  );
};

type ViewCellContentProps = {
  children: ReactNode;
  formattedOriginalValue?: ReactNode;
  originalValue?: string | number | boolean | null;
  variant: ViewCellVariant;
};

const ViewCellContent = ({ children, formattedOriginalValue, originalValue, variant }: ViewCellContentProps) => {
  if (variant === 'ai-suggested-modified') {
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        {originalValue != null ? (
          <span className="truncate text-xs leading-4 text-gray-400 line-through">
            {formattedOriginalValue ?? String(originalValue)}
          </span>
        ) : null}
        <div className="text-sky-800">{children}</div>
      </div>
    );
  }
  if (variant === 'ai-suggested-added') {
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="text-sky-800">{children}</div>
      </div>
    );
  }
  if (variant === 'ai-edited') {
    return <div className="min-w-0 flex-1">{children}</div>;
  }
  return children;
};
